import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Hotel, Key, Calendar as CalendarIcon, Users, MapPin, Search, BedDouble, Check, ArrowRight, ArrowLeft, CreditCard } from "lucide-react";

interface ApiConfig {
  apikey: string;
  secret: string;
  accountName: string;
  environment: "sandbox" | "production";
  allowRequests: number;
  perSeconds: number;
  throttling: boolean;
  throttleInterval: number;
  throttleRetryLimit: number;
  maxQuotas: number;
  quotaResetEvery: "day" | "week" | "month";
  alias: string;
}

const defaultConfig: ApiConfig = {
  apikey: "",
  secret: "",
  accountName: "",
  environment: "sandbox",
  allowRequests: 60,
  perSeconds: 60,
  throttling: true,
  throttleInterval: 1000,
  throttleRetryLimit: 3,
  maxQuotas: 10000,
  quotaResetEvery: "month",
  alias: "",
};

const STORAGE_KEY = "hotel_booking_api_config";

export default function HotelBookingApi() {
  const [config, setConfig] = useState<ApiConfig>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultConfig, ...JSON.parse(saved) } : defaultConfig;
    } catch {
      return defaultConfig;
    }
  });

  const [search, setSearch] = useState({
    city: "",
    checkIn: "",
    checkOut: "",
    guests: 2,
    rooms: 1,
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[] | null>(null);

  // تدفق الحجز
  const [bookingHotel, setBookingHotel] = useState<any | null>(null);
  const [bookingStep, setBookingStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  const [bookingDates, setBookingDates] = useState({ checkIn: "", checkOut: "" });
  const [guestInfo, setGuestInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    nationality: "",
    specialRequests: "",
    paymentMethod: "card" as "card" | "bank" | "cash",
  });
  const [confirmation, setConfirmation] = useState<{ ref: string } | null>(null);

  const roomTypesFor = (h: any) => [
    { id: "standard", name: "غرفة قياسية", capacity: 2, multiplier: 1, perks: ["سرير مزدوج", "إفطار اختياري", "واي فاي مجاني"] },
    { id: "deluxe", name: "غرفة ديلوكس", capacity: 3, multiplier: 1.35, perks: ["إطلالة على المدينة", "إفطار مجاني", "خدمة الغرف"] },
    { id: "suite", name: "جناح فاخر", capacity: 4, multiplier: 1.9, perks: ["غرفة جلوس منفصلة", "إفطار + عشاء", "دخول الصالة التنفيذية"] },
  ];

  const openBooking = (h: any) => {
    setBookingHotel(h);
    setBookingStep(1);
    setSelectedRoomId("standard");
    setBookingDates({ checkIn: search.checkIn, checkOut: search.checkOut });
    setGuestInfo({ fullName: "", email: "", phone: "", nationality: "", specialRequests: "", paymentMethod: "card" });
    setConfirmation(null);
  };

  const bookingNights = (() => {
    if (!bookingDates.checkIn || !bookingDates.checkOut) return 0;
    const d = (new Date(bookingDates.checkOut).getTime() - new Date(bookingDates.checkIn).getTime()) / 86400000;
    return d > 0 ? d : 0;
  })();

  const selectedRoom = bookingHotel ? roomTypesFor(bookingHotel).find((r) => r.id === selectedRoomId) : null;
  const roomNightly = bookingHotel && selectedRoom ? Math.round(bookingHotel.price * selectedRoom.multiplier) : 0;
  const subTotal = roomNightly * bookingNights * (search.rooms || 1);
  const taxes = Math.round(subTotal * 0.15);
  const total = subTotal + taxes;

  const nextStep = () => {
    if (bookingStep === 1 && !selectedRoomId) return toast.error("اختر نوع الغرفة");
    if (bookingStep === 2 && (!bookingDates.checkIn || !bookingDates.checkOut || bookingNights <= 0))
      return toast.error("تواريخ غير صحيحة");
    if (bookingStep === 3 && (!guestInfo.fullName || !guestInfo.phone))
      return toast.error("الرجاء تعبئة اسم الضيف ورقم الهاتف");
    setBookingStep((s) => (s < 4 ? ((s + 1) as any) : s));
  };

  const confirmBooking = () => {
    const ref = "BK-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    setConfirmation({ ref });
    toast.success(`تم تأكيد الحجز برقم ${ref}`);
  };

  const update = <K extends keyof ApiConfig>(key: K, value: ApiConfig[K]) =>
    setConfig((c) => ({ ...c, [key]: value }));

  const saveConfig = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    toast.success("تم حفظ إعدادات API بنجاح");
  };

  const testConnection = async () => {
    if (!config.apikey || !config.secret) {
      toast.error("الرجاء إدخال Apikey و Secret أولاً");
      return;
    }
    toast.success(`الاتصال ناجح مع ${config.environment === "sandbox" ? "بيئة الاختبار" : "بيئة الإنتاج"}`);
  };

  const searchHotels = async () => {
    if (!config.apikey) {
      toast.error("الرجاء حفظ إعدادات API أولاً");
      return;
    }
    if (!search.city || !search.checkIn || !search.checkOut) {
      toast.error("الرجاء تعبئة المدينة وتواريخ الإقامة");
      return;
    }
    setLoading(true);
    // محاكاة استدعاء API
    setTimeout(() => {
      setResults([
        { name: `فندق ${search.city} بلازا`, stars: 5, price: 420, currency: "SAR", rating: 9.1 },
        { name: `${search.city} جراند هوتيل`, stars: 4, price: 310, currency: "SAR", rating: 8.6 },
        { name: `بوتيك ${search.city}`, stars: 4, price: 265, currency: "SAR", rating: 8.2 },
        { name: `${search.city} إن`, stars: 3, price: 180, currency: "SAR", rating: 7.8 },
      ]);
      setLoading(false);
    }, 800);
  };

  const nights = (() => {
    if (!search.checkIn || !search.checkOut) return 0;
    const d = (new Date(search.checkOut).getTime() - new Date(search.checkIn).getTime()) / 86400000;
    return d > 0 ? d : 0;
  })();

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10">
          <Hotel className="h-7 w-7 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">حجز الفنادق عبر API</h1>
          <p className="text-muted-foreground">إعداد مفاتيح API والبحث عن الفنادق وحجزها</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Config */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" /> Apikey Detail
            </CardTitle>
            <CardDescription>إعدادات الاتصال مع مزود الفنادق</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Apikey</Label>
                <Input value={config.apikey} onChange={(e) => update("apikey", e.target.value)} placeholder="xxx-xxx-xxx" />
              </div>
              <div className="space-y-2">
                <Label>Secret</Label>
                <Input type="password" value={config.secret} onChange={(e) => update("secret", e.target.value)} placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label>Account Name</Label>
                <Input value={config.accountName} onChange={(e) => update("accountName", e.target.value)} placeholder="my-account" />
              </div>
              <div className="space-y-2">
                <Label>Environment</Label>
                <Select value={config.environment} onValueChange={(v) => update("environment", v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Rate Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Allow number of requests</Label>
                  <Input type="number" value={config.allowRequests} onChange={(e) => update("allowRequests", +e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Per (seconds)</Label>
                  <Input type="number" value={config.perSeconds} onChange={(e) => update("perSeconds", +e.target.value)} />
                </div>
              </div>
            </div>

            <Separator />
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Throttling</h3>
                <Switch checked={config.throttling} onCheckedChange={(v) => update("throttling", v)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Throttle interval (ms)</Label>
                  <Input type="number" disabled={!config.throttling} value={config.throttleInterval} onChange={(e) => update("throttleInterval", +e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Throttle retry limit</Label>
                  <Input type="number" disabled={!config.throttling} value={config.throttleRetryLimit} onChange={(e) => update("throttleRetryLimit", +e.target.value)} />
                </div>
              </div>
            </div>

            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Usage Quotas</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Quotas</Label>
                  <Input type="number" value={config.maxQuotas} onChange={(e) => update("maxQuotas", +e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Quota resets every</Label>
                  <Select value={config.quotaResetEvery} onValueChange={(v) => update("quotaResetEvery", v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">يومياً</SelectItem>
                      <SelectItem value="week">أسبوعياً</SelectItem>
                      <SelectItem value="month">شهرياً</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />
            <div className="space-y-2">
              <Label>Create Alias</Label>
              <Input value={config.alias} onChange={(e) => update("alias", e.target.value)} placeholder="مثال: مزود-الفنادق-الرئيسي" />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={saveConfig} className="flex-1">حفظ الإعدادات</Button>
              <Button variant="outline" onClick={testConnection}>اختبار الاتصال</Button>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" /> البحث عن الفنادق
            </CardTitle>
            <CardDescription>أدخل تفاصيل الإقامة للبحث</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><MapPin className="h-4 w-4" /> المدينة / الوجهة</Label>
              <Input value={search.city} onChange={(e) => setSearch({ ...search, city: e.target.value })} placeholder="الرياض، دبي، إسطنبول..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><CalendarIcon className="h-4 w-4" /> تاريخ الدخول</Label>
                <Input type="date" value={search.checkIn} onChange={(e) => setSearch({ ...search, checkIn: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><CalendarIcon className="h-4 w-4" /> تاريخ المغادرة</Label>
                <Input type="date" value={search.checkOut} onChange={(e) => setSearch({ ...search, checkOut: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Users className="h-4 w-4" /> عدد الضيوف</Label>
                <Input type="number" min={1} value={search.guests} onChange={(e) => setSearch({ ...search, guests: +e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>عدد الغرف</Label>
                <Input type="number" min={1} value={search.rooms} onChange={(e) => setSearch({ ...search, rooms: +e.target.value })} />
              </div>
            </div>
            {nights > 0 && (
              <p className="text-sm text-muted-foreground">عدد الليالي: <span className="font-semibold text-foreground">{nights}</span></p>
            )}
            <Button onClick={searchHotels} disabled={loading} className="w-full">
              {loading ? "جارٍ البحث..." : "بحث عن الفنادق"}
            </Button>

            {results && (
              <div className="space-y-3 pt-2">
                <h3 className="font-semibold">النتائج ({results.length})</h3>
                {results.map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/40 transition">
                    <div>
                      <p className="font-semibold">{h.name}</p>
                      <p className="text-xs text-muted-foreground">{"★".repeat(h.stars)} · تقييم {h.rating}</p>
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-primary">{h.price} {h.currency}</p>
                      <p className="text-xs text-muted-foreground">لكل ليلة</p>
                      <Button size="sm" className="mt-1" onClick={() => openBooking(h)}>احجز الآن</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* تدفق الحجز */}
      <Dialog open={!!bookingHotel} onOpenChange={(o) => !o && setBookingHotel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hotel className="h-5 w-5" /> حجز {bookingHotel?.name}
            </DialogTitle>
            <DialogDescription>
              أكمل الخطوات التالية لإتمام الحجز
            </DialogDescription>
          </DialogHeader>

          {/* مؤشر الخطوات */}
          {!confirmation && (
            <div className="flex items-center justify-between mb-4">
              {[
                { n: 1, label: "الغرفة" },
                { n: 2, label: "التواريخ" },
                { n: 3, label: "بيانات الضيف" },
                { n: 4, label: "الملخص" },
              ].map((st, idx) => (
                <div key={st.n} className="flex items-center flex-1">
                  <div className={`flex flex-col items-center ${bookingStep >= st.n ? "text-primary" : "text-muted-foreground"}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${bookingStep >= st.n ? "border-primary bg-primary text-primary-foreground" : "border-muted"}`}>
                      {bookingStep > st.n ? <Check className="h-4 w-4" /> : st.n}
                    </div>
                    <span className="text-xs mt-1">{st.label}</span>
                  </div>
                  {idx < 3 && <div className={`flex-1 h-0.5 mx-2 ${bookingStep > st.n ? "bg-primary" : "bg-muted"}`} />}
                </div>
              ))}
            </div>
          )}

          {/* الخطوة 1: الغرفة */}
          {!confirmation && bookingStep === 1 && bookingHotel && (
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><BedDouble className="h-4 w-4" /> اختر نوع الغرفة</h3>
              <RadioGroup value={selectedRoomId} onValueChange={setSelectedRoomId} className="space-y-2">
                {roomTypesFor(bookingHotel).map((room) => {
                  const nightly = Math.round(bookingHotel.price * room.multiplier);
                  return (
                    <Label
                      key={room.id}
                      htmlFor={room.id}
                      className={`flex items-start justify-between p-4 border rounded-lg cursor-pointer transition ${selectedRoomId === room.id ? "border-primary bg-primary/5" : "hover:bg-accent/40"}`}
                    >
                      <div className="flex gap-3">
                        <RadioGroupItem value={room.id} id={room.id} className="mt-1" />
                        <div>
                          <p className="font-semibold">{room.name}</p>
                          <p className="text-xs text-muted-foreground">يستوعب حتى {room.capacity} أشخاص</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {room.perks.map((p) => (
                              <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-primary">{nightly} {bookingHotel.currency}</p>
                        <p className="text-xs text-muted-foreground">لكل ليلة</p>
                      </div>
                    </Label>
                  );
                })}
              </RadioGroup>
            </div>
          )}

          {/* الخطوة 2: التواريخ */}
          {!confirmation && bookingStep === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><CalendarIcon className="h-4 w-4" /> تواريخ الإقامة</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>تاريخ الدخول</Label>
                  <Input type="date" value={bookingDates.checkIn} onChange={(e) => setBookingDates({ ...bookingDates, checkIn: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>تاريخ المغادرة</Label>
                  <Input type="date" value={bookingDates.checkOut} onChange={(e) => setBookingDates({ ...bookingDates, checkOut: e.target.value })} />
                </div>
              </div>
              {bookingNights > 0 && (
                <div className="p-3 bg-accent/40 rounded-lg text-sm">
                  <p>عدد الليالي: <span className="font-bold">{bookingNights}</span></p>
                  <p>عدد الغرف: <span className="font-bold">{search.rooms}</span></p>
                  <p>عدد الضيوف: <span className="font-bold">{search.guests}</span></p>
                </div>
              )}
            </div>
          )}

          {/* الخطوة 3: بيانات الضيف */}
          {!confirmation && bookingStep === 3 && (
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4" /> بيانات الضيف الرئيسي</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>الاسم الكامل *</Label>
                  <Input value={guestInfo.fullName} onChange={(e) => setGuestInfo({ ...guestInfo, fullName: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>رقم الهاتف *</Label>
                  <Input value={guestInfo.phone} onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>البريد الإلكتروني</Label>
                  <Input type="email" value={guestInfo.email} onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>الجنسية</Label>
                  <Input value={guestInfo.nationality} onChange={(e) => setGuestInfo({ ...guestInfo, nationality: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>طلبات خاصة</Label>
                <Textarea rows={2} value={guestInfo.specialRequests} onChange={(e) => setGuestInfo({ ...guestInfo, specialRequests: e.target.value })} placeholder="سرير إضافي، غرفة هادئة، طابق علوي..." />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><CreditCard className="h-4 w-4" /> طريقة الدفع</Label>
                <Select value={guestInfo.paymentMethod} onValueChange={(v) => setGuestInfo({ ...guestInfo, paymentMethod: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">بطاقة ائتمان</SelectItem>
                    <SelectItem value="bank">تحويل بنكي</SelectItem>
                    <SelectItem value="cash">الدفع عند الوصول</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* الخطوة 4: الملخص */}
          {!confirmation && bookingStep === 4 && bookingHotel && selectedRoom && (
            <div className="space-y-3">
              <h3 className="font-semibold">ملخص الحجز</h3>
              <div className="border rounded-lg divide-y">
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الفندق</span><span className="font-semibold">{bookingHotel.name}</span></div>
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الغرفة</span><span className="font-semibold">{selectedRoom.name}</span></div>
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الدخول / المغادرة</span><span className="font-semibold">{bookingDates.checkIn} ← {bookingDates.checkOut}</span></div>
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الليالي × الغرف</span><span className="font-semibold">{bookingNights} × {search.rooms}</span></div>
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الضيف</span><span className="font-semibold">{guestInfo.fullName}</span></div>
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الهاتف</span><span className="font-semibold">{guestInfo.phone}</span></div>
                <div className="p-3 flex justify-between"><span className="text-muted-foreground">الدفع</span><span className="font-semibold">{guestInfo.paymentMethod === "card" ? "بطاقة ائتمان" : guestInfo.paymentMethod === "bank" ? "تحويل بنكي" : "عند الوصول"}</span></div>
              </div>
              <div className="border rounded-lg p-3 space-y-1 bg-accent/30">
                <div className="flex justify-between text-sm"><span>سعر الليلة</span><span>{roomNightly} {bookingHotel.currency}</span></div>
                <div className="flex justify-between text-sm"><span>الإجمالي الفرعي</span><span>{subTotal} {bookingHotel.currency}</span></div>
                <div className="flex justify-between text-sm"><span>الضرائب (15%)</span><span>{taxes} {bookingHotel.currency}</span></div>
                <Separator />
                <div className="flex justify-between font-bold text-lg pt-1"><span>المجموع</span><span className="text-primary">{total} {bookingHotel.currency}</span></div>
              </div>
            </div>
          )}

          {/* شاشة التأكيد */}
          {confirmation && (
            <div className="text-center py-6 space-y-3">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/15 flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold">تم تأكيد الحجز بنجاح!</h3>
              <p className="text-muted-foreground">رقم الحجز المرجعي</p>
              <p className="text-2xl font-mono font-bold text-primary">{confirmation.ref}</p>
              <p className="text-sm text-muted-foreground">تم إرسال تفاصيل الحجز إلى {guestInfo.email || guestInfo.phone}</p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {confirmation ? (
              <Button onClick={() => setBookingHotel(null)} className="w-full">إغلاق</Button>
            ) : (
              <>
                {bookingStep > 1 && (
                  <Button variant="outline" onClick={() => setBookingStep((s) => (s - 1) as any)}>
                    <ArrowRight className="h-4 w-4 ml-1" /> السابق
                  </Button>
                )}
                {bookingStep < 4 ? (
                  <Button onClick={nextStep}>التالي <ArrowLeft className="h-4 w-4 mr-1" /></Button>
                ) : (
                  <Button onClick={confirmBooking} className="bg-green-600 hover:bg-green-700">
                    <Check className="h-4 w-4 ml-1" /> تأكيد الحجز
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}